import type { GetStaffMeResponse } from "@wallyo/contracts/console";
import type { DepartmentId } from "@wallyo/contracts/workspaces";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getScreenCounts, signOutStaff } from "../api/console-api";
import { useApi } from "../api/context";
import { ConsoleApiError } from "../api/errors";
import { createIntent } from "../api/idempotency";
import { setLanguage, t } from "../i18n";
import { formatTime } from "../i18n/format";
import { effectiveLanguage, useLanguage } from "../i18n/session-language";
import {
  landingWorkspace,
  overviewOfWorkspace,
  resolveRoute,
  tabsAreRows,
  workspaceShowing,
  workspacesOf,
  type Route,
} from "../navigation/screens";
import { useHash } from "../navigation/use-hash";
import { useLoad } from "../screens/config-parts";
import { AuditScreen } from "../screens/AuditScreen";
import { BillingScreen } from "../screens/billing/BillingScreen";
import { CasesScreen } from "../screens/cases/CasesScreen";
import { CustomersScreen } from "../screens/customers/CustomersScreen";
import { DashboardScreen } from "../screens/dashboard/DashboardScreen";
import { FeaturesScreen } from "../screens/features/FeaturesScreen";
import { TeamsScreen } from "../screens/teams/TeamsScreen";
import { RolesScreen } from "../screens/roles/RolesScreen";
import { RulesScreen } from "../screens/rules/RulesScreen";
import { PermissionsScreen } from "../screens/permissions/PermissionsScreen";
import { ActionsScreen } from "../screens/actions/ActionsScreen";
import { UserSettingsScreen } from "../screens/settings/UserSettingsScreen";
import { PaymentsScreen } from "../screens/payments/PaymentsScreen";
import { ReportsScreen } from "../screens/reports/ReportsScreen";
import { HrScreen } from "../screens/hr/HrScreen";
import { OverviewScreen } from "../screens/overview/OverviewScreen";
import { IncidentsScreen } from "../screens/incidents/IncidentsScreen";
import { ExperimentsScreen } from "../screens/experiments/ExperimentsScreen";
import { CampaignsScreen, LeadsScreen, ReferralsScreen } from "../screens/growth/GrowthScreens";
import {
  MarketplaceCategories,
  MarketplaceJobs,
  MarketplaceOverview,
  MarketplaceProviders,
  MarketplaceReviews,
} from "../screens/marketplace/MarketplaceScreen";
import { VerificationsScreen } from "../screens/verifications/VerificationsScreen";
import { ConnectionBanner } from "./ConnectionBanner";
import { ErrorNotice } from "./ErrorNotice";
import { LockedScreen } from "./LockedScreen";
import { PageTop } from "./PageTop";
import { ProfileScreen } from "../screens/ProfileScreen";
import { Sidebar } from "./Sidebar";
import { MyRequestsScreen } from "../screens/access/MyRequestsScreen";
import { DirectoryScreen } from "../screens/hr/DirectoryScreen";
import { MyTimeOffScreen } from "../screens/hr/MyTimeOffScreen";
import { WorkspaceHeader } from "./WorkspaceHeader";
import { StaffContext, type StaffContextValue } from "./staff-context";
import { useStaffMe } from "./use-staff-me";

/** Follows `settings.consoleTheme`: "system" leaves it to prefers-color-scheme. */
export function applyTheme(theme: GetStaffMeResponse["settings"]["consoleTheme"]): void {
  const root = document.documentElement;
  if (theme === "system") delete root.dataset.theme;
  else root.dataset.theme = theme;
}

export function Shell({ onSignedOut }: { onSignedOut: (notice: unknown) => void }) {
  const { client, session } = useApi();
  const { me, loadedAt, error, loading, reload } = useStaffMe();
  const hash = useHash();
  const [signingOut, setSigningOut] = useState(false);
  const signOutIntent = useRef(createIntent());
  // Which workspace is open. Not in the hash: every existing link is `#screen`
  // and a screen knows which workspaces list it, so the hash never had to
  // carry this (WALLIYO-0132).
  const [chosen, setChosen] = useState<DepartmentId | null>(null);

  // One call draws every badge. Reloaded with `me`, so a count is as fresh as
  // the features beside it.
  const badges = useLoad(() => getScreenCounts(client), "screen-counts");
  const counts = badges.state.data?.counts;

  const departments = useMemo(() => (me?.staffMember.roles ?? []).map((role) => role.department), [me]);
  const workspaces = useMemo(() => (me ? workspacesOf(departments, me.features) : []), [departments, me]);
  // You land in the workspace your role belongs to, until you pick another.
  const workspace = chosen ?? (me ? landingWorkspace(departments, me.features) : null);
  /**
   * Choosing a workspace takes you into it, at its front door.
   *
   * Setting the state alone was not enough: the hash still named the screen you
   * were on, that screen belongs to the workspace you were leaving, and
   * `workspaceShowing` correctly sent the header back there — so the switcher
   * appeared to do nothing. Moving the hash is what makes the console become
   * that department's console (WALLIYO-0134).
   */
  const setWorkspace = (id: DepartmentId) => {
    setChosen(id);
    const front = me ? overviewOfWorkspace(id, me.features) : null;
    window.location.hash = front ? front.id : "";
  };

  // Re-renders every screen in the new language when it changes.
  useLanguage();
  // Before paint, so a staff member reading Arabic never sees a French frame.
  useLayoutEffect(() => {
    if (!me) return;
    applyTheme(me.settings.consoleTheme);
    setLanguage(effectiveLanguage(me.settings.consoleLanguage));
  }, [me]);
  useEffect(() => () => applyTheme("system"), []);

  const staff = useMemo<StaffContextValue | null>(() => {
    if (!me) return null;
    const signOut = async () => {
      setSigningOut(true);
      let notice: unknown = null;
      try {
        await signOutStaff(client, { idempotencyKey: signOutIntent.current.key() });
      } catch (failure) {
        // Signing out always ends the session here. If the server did not
        // confirm, say so: the server-side session will end by inactivity.
        if (!(failure instanceof ConsoleApiError && failure.endsSession)) notice = failure;
      }
      onSignedOut(notice);
      session.clear();
    };
    return { me, signOut: () => void signOut(), signingOut };
  }, [me, client, session, onSignedOut, signingOut]);

  if (!staff) {
    return (
      <div className="center">
        <div className="auth-card">
          <ConnectionBanner />
          {error ? (
            <>
              <h3>{t("shell.loadFailed")}</h3>
              <ErrorNotice error={error} />
              <button className="btn primary" type="button" onClick={reload} disabled={loading}>
                {t("shell.retry")}
              </button>
            </>
          ) : (
            <div className="banner" role="status">
              <span className="spinner" aria-hidden="true" />
              <span>{t("shell.loading")}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // The workspace decides the front door, so signing in lands on its
  // Overview rather than on whichever business screen sorted first.
  const route = resolveRoute(hash, staff.me.features, workspace);
  const open = route.kind === "screen" || route.kind === "locked" ? route.screen : null;
  // A hash for a screen this workspace does not list moves the workspace to
  // one that does — following a link out of Finance into Engineering should
  // arrive in Engineering, not leave the header lying about where you are.
  const showing = open ? workspaceShowing(open, workspace, workspaces, staff.me.features) : workspace;

  return (
    <StaffContext.Provider value={staff}>
      <div className="app">
        <WorkspaceHeader
          current={showing}
          available={workspaces}
          onSwitch={setWorkspace}
          who={staff.me.staffMember.name}
          onProfile={route.kind === "profile"}
        />
        <Sidebar me={staff.me} workspace={showing} current={open} counts={counts} hash={hash} />
        <main>
          <ConnectionBanner />
          {error ? (
            <div className="banner bad" role="alert">
              <span>
                {t("shell.refreshFailed", {
                  time: loadedAt ? formatTime(loadedAt) : "",
                })}
              </span>
              <button className="btn" type="button" onClick={reload} disabled={loading}>
                {t("shell.retry")}
              </button>
            </div>
          ) : null}
          {error ? <ErrorNotice error={error} /> : null}
          <RouteView
            route={route}
            hash={hash}
            workspace={showing}
            roles={staff.me.staffMember.roles.map((role) => role.name)}
          />
        </main>
      </div>
    </StaffContext.Provider>
  );
}

/** `hash` reaches the Overviews because a tab of one lives in it. */
function RouteView({
  route,
  roles,
  hash,
  workspace,
}: {
  route: Route;
  roles: string[];
  hash: string;
  /** Which workspace is showing, so a screen knows if its tabs are already rows. */
  workspace: DepartmentId | null;
}) {
  switch (route.kind) {
    case "empty":
      return (
        <>
          <PageTop title={`${t("brand.name")} ${t("brand.console")}`} sub="" />
          {/* With no roles at all, naming them produced "your roles ()" — empty
              parentheses, which is what every new member saw before roles could
              be given at invite time (WALLIYO-0122). */}
          <div className="denied">
            {roles.length === 0 ? t("shell.noRoles") : t("shell.noScreens", { roles: roles.join(", ") })}
          </div>
        </>
      );
    case "profile":
      return <ProfileScreen key="profile" />;
    case "locked":
      return <LockedScreen screen={route.screen} />;
    case "screen":
      switch (route.screen.id) {
        case "dashboard":
          return <DashboardScreen key="dashboard" />;
        case "audit":
          return <AuditScreen key="audit" />;
        case "hr":
          return <HrScreen key="hr" tabsInSidebar={tabsAreRows(workspace, "hr")} />;
        case "peopleOverview":
          return <OverviewScreen key="peopleOverview" department="people" hash={hash} />;
        case "financeOverview":
          return <OverviewScreen key="financeOverview" department="finance" hash={hash} />;
        case "supportOverview":
          return <OverviewScreen key="supportOverview" department="support" hash={hash} />;
        case "trustOverview":
          return <OverviewScreen key="trustOverview" department="trust" hash={hash} />;
        case "adminOverview":
          return <OverviewScreen key="adminOverview" department="administration" hash={hash} />;
        case "productOverview":
          return <OverviewScreen key="productOverview" department="product" hash={hash} />;
        case "growthOverview":
          return <OverviewScreen key="growthOverview" department="growth" hash={hash} />;
        case "executive":
          return <OverviewScreen key="executive" department="executive" hash={hash} />;
        case "experiments":
          return <ExperimentsScreen key="experiments" />;
        case "leads":
          return <LeadsScreen key="leads" />;
        case "campaigns":
          return <CampaignsScreen key="campaigns" />;
        case "referrals":
          return <ReferralsScreen key="referrals" />;
        case "incidents":
          return <IncidentsScreen key="incidents" />;
        case "directory":
          return <DirectoryScreen key="directory" />;
        case "myTimeOff":
          return <MyTimeOffScreen key="myTimeOff" />;
        case "myRequests":
          return <MyRequestsScreen key="myRequests" />;
        case "marketplaceOverview":
          return <MarketplaceOverview key="marketplaceOverview" />;
        case "jobs":
          return <MarketplaceJobs key="jobs" />;
        case "providers":
          return <MarketplaceProviders key="providers" />;
        case "categories":
          return <MarketplaceCategories key="categories" />;
        case "marketplaceReviews":
          return <MarketplaceReviews key="marketplaceReviews" />;
        case "cases":
          return <CasesScreen key="cases" />;
        case "verifications":
          return <VerificationsScreen key="verifications" />;
        case "customer":
          return <CustomersScreen key="customer" />;
        case "features":
          return <FeaturesScreen key="features" />;
        case "permissions":
          return <PermissionsScreen key="permissions" />;
        case "actions":
          return <ActionsScreen key="actions" />;
        case "userSettings":
          return <UserSettingsScreen key="userSettings" />;
        case "teams":
          return <TeamsScreen key="teams" />;
        case "roles":
          return <RolesScreen key="roles" />;
        case "rules":
          return <RulesScreen key="rules" />;
        case "reports":
          return <ReportsScreen key="reports" />;
        case "payments":
          return <PaymentsScreen key="payments" />;
        case "billing":
          return <BillingScreen key="billing" />;
      }
  }
}
